import React from "react";
import { Card, Tag, Space, Typography, Button, Tooltip } from "antd";
import {
  PhoneOutlined,
  ClockCircleOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

const { Text, Title } = Typography;

const CallRequestCard = ({ callRequest, onClick, showActions = true }) => {
  // Business hours configuration
  const businessHoursConfig = {
    start: 9, // 9 AM
    end: 17, // 5 PM
    days: [1, 2, 3, 4, 5], // Monday = 1, Sunday = 0
    timezone: "local", // Could be configurable for different regions
  };

  // Helper function to calculate working hours SLA
  const calculateWorkingHoursSLA = (slaDueAt) => {
    if (!slaDueAt) return { status: "no_sla", message: "No SLA defined" };

    const now = new Date();
    const dueDate = new Date(slaDueAt);

    // Check if current time is within business hours
    const isBusinessHours = () => {
      const day = now.getDay();
      const hour = now.getHours();
      return (
        businessHoursConfig.days.includes(day) &&
        hour >= businessHoursConfig.start &&
        hour < businessHoursConfig.end
      );
    };

    // Calculate working minutes until SLA due
    const calculateWorkingMinutes = () => {
      let workingMinutes = 0;
      let current = new Date(now);

      while (current < dueDate) {
        const day = current.getDay();
        const hour = current.getHours();

        // Only count business hours on business days
        if (businessHoursConfig.days.includes(day)) {
          if (hour < businessHoursConfig.start) {
            // Before business hours, count from start of business day
            workingMinutes +=
              (businessHoursConfig.end - businessHoursConfig.start) * 60;
          } else if (
            hour >= businessHoursConfig.start &&
            hour < businessHoursConfig.end
          ) {
            // During business hours
            workingMinutes += (businessHoursConfig.end - hour) * 60;
          }
          // After business hours, don't count
        }

        // Move to next day
        current.setDate(current.getDate() + 1);
        current.setHours(businessHoursConfig.start, 0, 0, 0);
      }

      return workingMinutes;
    };

    const workingMinutes = calculateWorkingMinutes();
    const isOverdue = workingMinutes <= 0;

    if (isOverdue) {
      return {
        status: "breached",
        message: "SLA Breached",
        color: "#ff4d4f",
        workingMinutes: 0,
        isBusinessHours: isBusinessHours(),
      };
    }

    // Convert to hours and minutes for display
    const hours = Math.floor(workingMinutes / 60);
    const minutes = workingMinutes % 60;

    let status, color;
    if (workingMinutes <= 60) {
      // 1 hour or less
      status = "critical";
      color = "#ff4d4f";
    } else if (workingMinutes <= 240) {
      // 4 hours or less
      status = "warning";
      color = "#fa8c16";
    } else {
      status = "normal";
      color = "#52c41a";
    }

    return {
      status,
      message: `${hours}h ${minutes}m remaining`,
      color,
      workingMinutes,
      isBusinessHours: isBusinessHours(),
      displayText: hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`,
    };
  };

  const slaStatus = calculateWorkingHoursSLA(callRequest.slaDueAt);

  // Helper function to get agent name
  const getAgentName = (assignedToUserId, assignedToUser) => {
    if (!assignedToUserId) return null;

    if (assignedToUser) {
      if (assignedToUser.firstName && assignedToUser.lastName) {
        return `${assignedToUser.firstName} ${assignedToUser.lastName}`;
      } else if (assignedToUser.name) {
        return assignedToUser.name;
      } else if (assignedToUser.email) {
        return assignedToUser.email;
      }
    }

    return "Unknown Agent";
  };

  const getStatusColor = (status) => {
    const colors = {
      requested: "blue",
      assigned: "orange",
      calling: "purple",
      completed: "green",
      no_answer: "red",
      reschedule: "geekblue",
    };
    return colors[status] || "default";
  };

  const getPriorityColor = () => {
    return slaStatus.color;
  };

  return (
    <Card
      size="small"
      hoverable
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "default" }}
      extra={
        <Space>
          <Tag color={getStatusColor(callRequest.status)}>
            {callRequest.status.toUpperCase()}
          </Tag>
          {slaStatus.status === "breached" && (
            <Tooltip title="SLA Breached - Immediate action required">
              <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
            </Tooltip>
          )}
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        {/* Customer Info */}
        <div>
          <UserOutlined style={{ marginRight: 8 }} />
          <Text strong>{callRequest.customerId}</Text>
        </div>

        {/* Phone Number */}
        <div>
          <PhoneOutlined style={{ marginRight: 8 }} />
          <Text>{callRequest.phone}</Text>
        </div>

        {/* SLA Status */}
        <div>
          <Space>
            <ClockCircleOutlined
              style={{
                marginRight: 8,
                color: slaStatus.color,
              }}
            />
            <Text style={{ color: slaStatus.color }}>
              {slaStatus.status === "breached"
                ? "Overdue"
                : slaStatus.displayText}
            </Text>
            <Tooltip
              title={
                <div>
                  <div>
                    <strong>
                      SLA Status: {slaStatus.status.toUpperCase()}
                    </strong>
                  </div>
                  <div>• Working hours only (Mon-Fri, 9 AM - 5 PM)</div>
                  <div>
                    • Current:{" "}
                    {slaStatus.isBusinessHours
                      ? "🟢 Business Hours"
                      : "🟠 Outside Hours"}
                  </div>
                  <div>
                    •{" "}
                    {slaStatus.status === "breached"
                      ? "🚨 SLA Breached - Immediate action required"
                      : `⏰ ${slaStatus.displayText} remaining`}
                  </div>
                </div>
              }
              placement="top"
            >
              <InfoCircleOutlined
                style={{
                  color: "#1890ff",
                  cursor: "help",
                  fontSize: "14px",
                  marginLeft: "4px",
                }}
              />
            </Tooltip>
          </Space>
        </div>

        {/* Assignment */}
        {callRequest.assignedToUserId && (
          <div>
            <Space>
              <UserOutlined style={{ marginRight: 8 }} />
              <Text type="secondary">
                {getAgentName(
                  callRequest.assignedToUserId,
                  callRequest.assignedToUser
                )}
              </Text>
              <Tooltip
                title={
                  <div>
                    <div>
                      <strong>Agent Assignment</strong>
                    </div>
                    <div>• Assigned agent for this call</div>
                    <div>• Click to view full details</div>
                  </div>
                }
                placement="top"
              >
                <InfoCircleOutlined
                  style={{
                    color: "#1890ff",
                    cursor: "help",
                    fontSize: "14px",
                    marginLeft: "4px",
                  }}
                />
              </Tooltip>
            </Space>
          </div>
        )}

        {/* Actions */}
        {showActions && (
          <div style={{ marginTop: 8 }}>
            <Space>
              <Button
                size="small"
                type="primary"
                icon={<PhoneOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  // Handle call action
                }}
              >
                Call Now
              </Button>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  // Handle view details
                }}
              >
                View Details
              </Button>
            </Space>
          </div>
        )}
      </Space>
    </Card>
  );
};

export default CallRequestCard;
